import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Device } from 'mediasoup-client';
import {
  Box, Flex, Grid, Text, IconButton, VStack, HStack, Badge, Tooltip,
  Drawer, DrawerOverlay, DrawerContent, DrawerHeader, DrawerBody,
  Input, Button, useDisclosure, Avatar, useToast, Circle,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, FormControl, FormLabel,
} from '@chakra-ui/react';
import {
  FiMic, FiMicOff, FiVideo, FiVideoOff, FiMonitor, FiMessageSquare,
  FiPhoneOff, FiImage, FiUsers, FiUserX, FiLock, FiUnlock, FiDisc, FiRadio,
} from 'react-icons/fi';

function decodeToken(token) {
  try {
    // JWT uses base64url: no padding, - instead of +, _ instead of /
    let base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    while (base64.length % 4) base64 += '=';
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

// Fallback ICE servers used until /api/v1/ice-servers responds (STUN only —
// TURN credentials are fetched per-join since they're short-lived). Passed to
// mediasoup-client's transports exactly like a native RTCPeerConnection would.
const DEFAULT_ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
];

const MAX_ICE_RESTART_ATTEMPTS = 3;

// Moderator-only health dot next to the Recording/Live streaming badges —
// null covers both "no heartbeat yet" and the transport's own 'new' state,
// which is the normal starting point, not a problem.
function healthDotColor(state) {
  if (state === 'connected') return 'green.400';
  if (state === 'connecting' || state === 'new' || !state) return 'yellow.400';
  if (state === 'disconnected' || state === 'failed') return 'red.500';
  return 'gray.400'; // 'closed' or anything unrecognized
}

function healthDotLabel(state) {
  if (state === 'connected') return 'Recorder is actively capturing';
  if (state === 'connecting') return 'Recorder connecting...';
  if (state === 'new' || !state) return 'Waiting for recorder to connect...';
  if (state === 'disconnected' || state === 'failed') return 'Recorder disconnected — content may not be captured';
  return `Recorder state: ${state}`;
}

function ParticipantTile({ participant, isLocal, canModerate, locked, onMute, onLock, onRemove }) {
  return (
    <Box
      position="relative"
      bg="gray.800"
      borderRadius="xl"
      overflow="hidden"
      border="2px solid"
      borderColor={isLocal ? 'teal.500' : 'gray.700'}
      aspectRatio="16/9"
    >
      {participant.stream ? (
        <video
          ref={(el) => { if (el && participant.stream) el.srcObject = participant.stream; }}
          autoPlay
          muted={isLocal}
          playsInline
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <Flex w="full" h="full" align="center" justify="center" minH="120px">
          <Avatar name={participant.displayName} size="lg" bg="teal.500" />
        </Flex>
      )}

      <Box position="absolute" bottom={2} left={3}>
        <HStack spacing={2}>
          <Badge
            bg="blackAlpha.700"
            color="white"
            fontSize="xs"
            px={2}
            py={1}
            borderRadius="md"
          >
            {participant.displayName} {isLocal && '(You)'}
          </Badge>
          {participant.audioMuted && (
            <Box bg="red.500" borderRadius="full" p={1}>
              <FiMicOff size={10} color="white" />
            </Box>
          )}
        </HStack>
      </Box>

      {canModerate && !isLocal && (
        <Box position="absolute" top={2} right={2}>
          <HStack spacing={1}>
            <Tooltip label={participant.audioMuted ? 'Unmute participant' : 'Mute participant'}>
              <IconButton
                icon={participant.audioMuted ? <FiMicOff size={12} /> : <FiMic size={12} />}
                size="xs"
                borderRadius="full"
                bg="blackAlpha.700"
                color="white"
                _hover={{ bg: 'blackAlpha.800' }}
                onClick={() => onMute(participant.userId, !participant.audioMuted)}
                aria-label="Toggle participant mute"
              />
            </Tooltip>
            {participant.audioMuted && (
              <Tooltip label={locked ? 'Unlock (allow self-unmute)' : 'Lock mute (prevent self-unmute)'}>
                <IconButton
                  icon={locked ? <FiLock size={12} /> : <FiUnlock size={12} />}
                  size="xs"
                  borderRadius="full"
                  bg={locked ? 'orange.500' : 'blackAlpha.700'}
                  color="white"
                  _hover={{ bg: locked ? 'orange.400' : 'blackAlpha.800' }}
                  onClick={() => onLock(participant.userId, !locked)}
                  aria-label="Toggle mute lock"
                />
              </Tooltip>
            )}
            <Tooltip label="Remove participant">
              <IconButton
                icon={<FiUserX size={12} />}
                size="xs"
                borderRadius="full"
                bg="red.600"
                color="white"
                _hover={{ bg: 'red.500' }}
                onClick={() => onRemove(participant.userId, participant.displayName)}
                aria-label="Remove participant"
              />
            </Tooltip>
          </HStack>
        </Box>
      )}
    </Box>
  );
}

function ChatPanel({ messages, onSend }) {
  const [text, setText] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = () => {
    if (!text.trim()) return;
    onSend(text);
    setText('');
  };

  return (
    <Flex direction="column" h="full">
      <VStack flex={1} overflowY="auto" align="stretch" spacing={3} p={4}>
        {messages.map((msg, i) => (
          <Box key={i}>
            <Text fontSize="xs" fontWeight="600" color="teal.300">{msg.displayName}</Text>
            <Box bg="gray.700" borderRadius="lg" px={3} py={2} mt={1}>
              <Text fontSize="sm" color="white">{msg.message}</Text>
            </Box>
          </Box>
        ))}
        <div ref={messagesEndRef} />
      </VStack>
      <HStack p={4} borderTop="1px solid" borderColor="gray.700">
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSend()}
          placeholder="Send a message..."
          bg="gray.700"
          border="none"
          color="white"
          _placeholder={{ color: 'gray.400' }}
          size="sm"
        />
        <Button colorScheme="teal" size="sm" onClick={handleSend}>Send</Button>
      </HStack>
    </Flex>
  );
}

function ParticipantsPanel({ participants, canModerate, lockedMap, onMute, onLock, onRemove }) {
  return (
    <VStack align="stretch" spacing={0} divider={<Box borderBottom="1px solid" borderColor="gray.700" />}>
      {participants.map((p) => (
        <HStack key={p.userId} px={4} py={3} justify="space-between">
          <HStack spacing={2}>
            <Avatar name={p.displayName} size="sm" bg="teal.500" />
            <Text color="white" fontSize="sm">
              {p.displayName} {p.isLocal && '(You)'}
            </Text>
            {p.audioMuted && <FiMicOff size={12} color="#fc8181" />}
          </HStack>
          {canModerate && !p.isLocal && (
            <HStack spacing={1}>
              <Tooltip label={p.audioMuted ? 'Unmute' : 'Mute'}>
                <IconButton
                  icon={p.audioMuted ? <FiMicOff size={12} /> : <FiMic size={12} />}
                  size="xs"
                  variant="ghost"
                  colorScheme="whiteAlpha"
                  color="white"
                  onClick={() => onMute(p.userId, !p.audioMuted)}
                  aria-label="Toggle mute"
                />
              </Tooltip>
              {p.audioMuted && (
                <Tooltip label={lockedMap[p.userId] ? 'Unlock' : 'Lock mute'}>
                  <IconButton
                    icon={lockedMap[p.userId] ? <FiLock size={12} /> : <FiUnlock size={12} />}
                    size="xs"
                    variant="ghost"
                    colorScheme={lockedMap[p.userId] ? 'orange' : 'whiteAlpha'}
                    color={lockedMap[p.userId] ? 'orange.300' : 'white'}
                    onClick={() => onLock(p.userId, !lockedMap[p.userId])}
                    aria-label="Toggle mute lock"
                  />
                </Tooltip>
              )}
              <Tooltip label="Remove">
                <IconButton
                  icon={<FiUserX size={12} />}
                  size="xs"
                  variant="ghost"
                  colorScheme="red"
                  onClick={() => onRemove(p.userId, p.displayName)}
                  aria-label="Remove participant"
                />
              </Tooltip>
            </HStack>
          )}
        </HStack>
      ))}
      {participants.length === 0 && (
        <Text color="gray.500" fontSize="sm" p={4} textAlign="center">No one else is here yet.</Text>
      )}
    </VStack>
  );
}

export default function VideoRoom() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();

  const roomId = searchParams.get('roomId');
  const joinToken = searchParams.get('token');
  const displayName = searchParams.get('displayName') || 'Guest';
  const urlUserId = searchParams.get('userId');

  const [audioMuted, setAudioMuted] = useState(false);
  const [videoMuted, setVideoMuted] = useState(false);
  const [screenSharing, setScreenSharing] = useState(false);
  const [imageSharing, setImageSharing] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [mediaReady, setMediaReady] = useState(false);
  const [iceServersReady, setIceServersReady] = useState(false);

  const localStreamRef = useRef(null);
  const screenStreamRef = useRef(null);
  const imageStreamRef = useRef(null);
  const imageInputRef = useRef(null);
  const iceServersRef = useRef(DEFAULT_ICE_SERVERS);
  const decodedToken = decodeToken(joinToken);
  const localUserId = useRef(decodedToken?.user_id || urlUserId || `user_${Date.now()}`);
  // e.g. a webinar attendee's token carries permissions.publish: false
  // (meet/meet/views.py) — a view-only participant. Missing/malformed
  // permissions default to publishing allowed (matches issue_join_token's
  // and self_join_token's own defaulting); meet_sfu enforces the real
  // security boundary server-side regardless of what this says.
  const canPublish = useRef(decodedToken?.permissions?.publish ?? true);
  // Host-only moderation controls (mute/lock/remove another participant) —
  // meet_sfu/src/signaling.js and meet/meet/consumers.py are the real
  // enforcement; this only gates whether the UI offers the buttons.
  const canModerate = useRef(decodedToken?.permissions?.moderate ?? false);
  const isChatOpenRef = useRef(false);
  // Removal is signaled independently by both meet-sfu (removedFromRoom) and
  // Django (removed_from_room, when its own force_remove closes this socket)
  // — this just stops the second one from double-toasting/re-navigating.
  const hasBeenRemovedRef = useRef(false);
  // True when a host has muted *this* participant with the lock set — blocks
  // this client's own unmute button. The real block is server-side
  // (meet_sfu rejects resumeProducer while locked); this is just the UI
  // reflecting it so the button doesn't look like it should work.
  const [muteLockedByHost, setMuteLockedByHost] = useState(false);
  // Host's own optimistic view of who they've locked — there's no
  // server-side query for "current lock state of everyone" (see plan's
  // known v1 limitation), so this only reflects locks *this* host session
  // has set, keyed by target userId.
  const [hostLockedMap, setHostLockedMap] = useState({});

  // Room-level enablement (admin's Rooms.js checkboxes) — fetched once on
  // join via the same public room_info endpoint JoinRoom.js already uses
  // before joining; this only decides whether to offer the buttons at all,
  // same "UI gate, not the real boundary" role canModerate/canPublish play
  // above. meet_sfu's startRecording/startStreaming re-check the admin
  // config itself regardless of what this says.
  const [roomConfig, setRoomConfig] = useState({ recording_enabled: false, streaming_enabled: false, rtmp_locked: false });
  // Reflect meet-sfu's recordingStarted/streamingStarted broadcasts — these
  // reach every participant (not just moderators), which is also what
  // drives the "this call is being recorded" badge everyone sees below.
  const [isRecording, setIsRecording] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [recordingBusy, setRecordingBusy] = useState(false);
  const [streamingBusy, setStreamingBusy] = useState(false);
  // Real-time connection health from the recorder itself (meet-sfu's
  // 'recordingHealth' broadcast, sourced from recorderClient.js's
  // recvTransport.connectionstatechange, relayed over the same WS) — not
  // just "was it started," but "is it actually capturing right now."
  // Moderator-only (see the health dot below): a random attendee seeing
  // "recorder disconnected" can't do anything about it, a moderator can.
  const [recordingHealth, setRecordingHealth] = useState(null);
  const [streamingHealth, setStreamingHealth] = useState(null);
  const { isOpen: isStreamModalOpen, onOpen: onStreamModalOpen, onClose: onStreamModalClose } = useDisclosure();
  const [streamUrlInput, setStreamUrlInput] = useState('');
  const [streamKeyInput, setStreamKeyInput] = useState('');

  // Django /ws: roster (participant_joined/left), chat, mute-state, screenshare
  // notifications — unchanged by the mediasoup migration, still owned by
  // meet/meet/consumers.py.
  const rosterWsRef = useRef(null);

  // meet-sfu /ws: mediasoup signaling only (transports/producers/consumers).
  const sfuWsRef = useRef(null);
  const sfuPendingRef = useRef({}); // reqId -> {resolve, reject}
  const deviceRef = useRef(null);
  const sendTransportRef = useRef(null);
  const recvTransportRef = useRef(null);
  const audioProducerRef = useRef(null);
  const videoProducerRef = useRef(null);
  const consumersRef = useRef({}); // producerId -> Consumer
  const producerMetaRef = useRef({}); // producerId -> { userId, kind }
  const remoteTracksRef = useRef({}); // userId -> { audio: Track|null, video: Track|null }
  const iceRestartAttemptsRef = useRef({}); // transportId -> count
  // Mirrors audioMuted/videoMuted state without needing those in the SFU
  // effect's dependency array (which would tear down/rebuild the whole
  // mediasoup connection on every mute toggle otherwise).
  const currentMuteStateRef = useRef({ audioMuted: false, videoMuted: false });

  const { isOpen: isChatOpen, onOpen: onChatOpenBase, onClose: onChatClose } = useDisclosure();
  const { isOpen: isParticipantsOpen, onOpen: onParticipantsOpen, onClose: onParticipantsClose } = useDisclosure();

  const onChatOpen = useCallback(() => {
    setUnreadCount(0);
    isChatOpenRef.current = true;
    onChatOpenBase();
  }, [onChatOpenBase]);

  // Keep ref in sync when chat closes
  useEffect(() => {
    if (!isChatOpen) isChatOpenRef.current = false;
  }, [isChatOpen]);

  const getRosterWsUrl = useCallback(() => {
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${proto}//${window.location.host}/ws?token=${joinToken}`;
  }, [joinToken]);

  const getSfuWsUrl = useCallback(() => {
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    // meet-sfu is a separate service/ingress (infrastructure/k8s/app-unified.yaml:
    // meet-sfu-ingress) — meet.selwynevents.com's host swapped for
    // meet-sfu.selwynevents.com, same token.
    const sfuHost = window.location.host.replace(/^meet\./, 'meet-sfu.');
    return `${proto}//${sfuHost}/ws?token=${joinToken}`;
  }, [joinToken]);

  // Initialize local media
  useEffect(() => {
    const initMedia = async () => {
      if (!canPublish.current) {
        // View-only participant (e.g. a webinar attendee) — don't even
        // request camera/mic access, nothing will be produced with it.
        setParticipants([{
          userId: localUserId.current,
          displayName,
          stream: null,
          audioMuted: true,
          videoMuted: true,
          isLocal: true,
        }]);
        setMediaReady(true);
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        localStreamRef.current = stream;
        setParticipants([{
          userId: localUserId.current,
          displayName,
          stream,
          audioMuted: false,
          videoMuted: false,
          isLocal: true,
        }]);
      } catch (e) {
        toast({ title: 'Camera/mic access denied', status: 'warning', duration: 4000 });
        setParticipants([{
          userId: localUserId.current,
          displayName,
          stream: null,
          audioMuted: true,
          videoMuted: true,
          isLocal: true,
        }]);
      } finally {
        setMediaReady(true);
      }
    };
    initMedia();
    return () => {
      localStreamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, [displayName, toast]);

  // Fetch ICE servers (STUN + TURN, if configured) for this join — TURN credentials
  // are short-lived so this is re-fetched per join rather than hardcoded. Used as
  // the iceServers fallback on both mediasoup transports below.
  useEffect(() => {
    if (!joinToken) return;
    let cancelled = false;
    fetch(`/api/v1/ice-servers?token=${encodeURIComponent(joinToken)}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled && data?.ice_servers?.length) {
          iceServersRef.current = data.ice_servers;
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setIceServersReady(true); });
    return () => { cancelled = true; };
  }, [joinToken]);

  // Room-level recording/streaming enablement — see roomConfig's declaration
  // above for why this is a UI-only gate.
  useEffect(() => {
    if (!roomId) return;
    let cancelled = false;
    fetch(`/api/v1/rooms/${encodeURIComponent(roomId)}/info`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled && data) {
          setRoomConfig({
            recording_enabled: !!data.recording_enabled,
            streaming_enabled: !!data.streaming_enabled,
            rtmp_locked: !!data.rtmp_locked,
          });
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [roomId]);

  // Roster/chat/mute — Django /ws, unchanged in shape from before the mediasoup
  // migration except that it no longer carries offer/answer/ice_candidate
  // (that's now meet-sfu's job, in the effect below).
  useEffect(() => {
    if (!joinToken || !mediaReady) return;

    const ws = new WebSocket(getRosterWsUrl());
    rosterWsRef.current = ws;

    ws.onopen = () => {
      console.log('[Meet] Roster signaling connected');
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);

      switch (data.type) {
        case 'participant_joined':
          if (data.user_id !== localUserId.current) {
            setParticipants((prev) => {
              if (prev.find((p) => p.userId === data.user_id)) return prev;
              return [...prev, { userId: data.user_id, displayName: data.display_name, stream: null, audioMuted: false, videoMuted: false }];
            });
          }
          break;

        case 'participant_left':
          setParticipants((prev) => prev.filter((p) => p.userId !== data.user_id));
          // Defensive cleanup in case meet-sfu's own producerClosed events for
          // this user haven't arrived yet (e.g. an abrupt disconnect).
          delete remoteTracksRef.current[data.user_id];
          for (const [producerId, meta] of Object.entries(producerMetaRef.current)) {
            if (meta.userId === data.user_id) {
              consumersRef.current[producerId]?.close();
              delete consumersRef.current[producerId];
              delete producerMetaRef.current[producerId];
            }
          }
          break;

        case 'mute_state':
          setParticipants((prev) => prev.map((p) =>
            p.userId === data.user_id
              ? { ...p, audioMuted: data.audio_muted, videoMuted: data.video_muted }
              : p
          ));
          break;

        case 'chat_message':
          setChatMessages((prev) => [...prev, {
            userId: data.user_id,
            displayName: data.display_name,
            message: data.message,
          }]);
          if (!isChatOpenRef.current) {
            setUnreadCount((n) => n + 1);
          }
          break;

        case 'screenshare_started':
          toast({ title: `${data.display_name} started screen sharing`, status: 'info', duration: 3000 });
          break;

        case 'screenshare_stopped':
          toast({ title: 'Screen sharing stopped', status: 'info', duration: 2000 });
          break;

        case 'image_share_started':
          toast({ title: `${data.display_name} is sharing an image`, status: 'info', duration: 3000 });
          break;

        case 'image_share_stopped':
          toast({ title: `${data.display_name} stopped sharing image`, status: 'info', duration: 2000 });
          break;

        // Backup path for the same removal meet-sfu's removedFromRoom
        // already handles (see the guard there) — in case this socket's
        // force_remove closure lands first for some reason.
        case 'removed_from_room':
          if (hasBeenRemovedRef.current) break;
          hasBeenRemovedRef.current = true;
          toast({ title: 'You were removed from the meeting by the host', status: 'warning', duration: 4000 });
          localStreamRef.current?.getTracks().forEach((t) => t.stop());
          imageStreamRef.current?.getTracks().forEach((t) => t.stop());
          sfuWsRef.current?.close();
          navigate('/');
          break;

        default:
          break;
      }
    };

    ws.onclose = () => {
      console.log('[Meet] Roster signaling disconnected');
    };

    return () => {
      ws.close();
    };
  }, [joinToken, getRosterWsUrl, toast, mediaReady, navigate]);

  // Rebuilds participant.stream from whatever audio/video consumer tracks we
  // currently have for that user. Called every time a consumer for them
  // arrives or closes — a fresh MediaStream is constructed each time (rather
  // than mutating one in place) so React sees a new object and re-renders.
  const updateParticipantStream = useCallback((userId) => {
    const tracks = remoteTracksRef.current[userId];
    const trackList = tracks ? [tracks.audio, tracks.video].filter(Boolean) : [];
    const stream = trackList.length ? new MediaStream(trackList) : null;
    setParticipants((prev) => prev.map((p) => (p.userId === userId ? { ...p, stream } : p)));
  }, []);

  const sfuRpc = useCallback((type, data) => {
    return new Promise((resolve, reject) => {
      if (sfuWsRef.current?.readyState !== WebSocket.OPEN) {
        reject(new Error('SFU signaling socket is not open'));
        return;
      }
      const reqId = Math.random().toString(36).slice(2);
      sfuPendingRef.current[reqId] = { resolve, reject };
      sfuWsRef.current.send(JSON.stringify({ reqId, type, data }));
    });
  }, []);

  const handleTransportStateChange = useCallback((transport, state) => {
    if (state === 'connected') {
      iceRestartAttemptsRef.current[transport.id] = 0;
      return;
    }
    if (state !== 'failed') return;

    const attempts = (iceRestartAttemptsRef.current[transport.id] || 0) + 1;
    iceRestartAttemptsRef.current[transport.id] = attempts;
    if (attempts > MAX_ICE_RESTART_ATTEMPTS) {
      console.warn(`[Meet] Giving up reconnecting transport ${transport.id} after ${attempts} attempts`);
      return;
    }
    console.warn(`[Meet] Transport ${transport.id} connection failed, restarting ICE (attempt ${attempts})`);
    sfuRpc('restartIce', { transportId: transport.id })
      .then(({ iceParameters }) => transport.restartIce({ iceParameters }))
      .catch((e) => console.warn('[Meet] ICE restart failed', e));
  }, [sfuRpc]);

  const consumeProducer = useCallback(async (producerId, remoteUserId, kind) => {
    if (!recvTransportRef.current || !deviceRef.current) return;
    try {
      const { id, producerId: pId, kind: consKind, rtpParameters } = await sfuRpc('consume', {
        transportId: recvTransportRef.current.id,
        producerId,
        rtpCapabilities: deviceRef.current.rtpCapabilities,
      });
      const consumer = await recvTransportRef.current.consume({ id, producerId: pId, kind: consKind, rtpParameters });
      consumersRef.current[producerId] = consumer;
      producerMetaRef.current[producerId] = { userId: remoteUserId, kind: consKind };
      if (!remoteTracksRef.current[remoteUserId]) {
        remoteTracksRef.current[remoteUserId] = { audio: null, video: null };
      }
      remoteTracksRef.current[remoteUserId][consKind] = consumer.track;
      updateParticipantStream(remoteUserId);
      await sfuRpc('resumeConsumer', { consumerId: consumer.id });
    } catch (e) {
      console.warn('[Meet] Failed to consume producer', producerId, e);
    }
  }, [sfuRpc, updateParticipantStream]);

  // Host moderation — real enforcement happens in meet_sfu (pausing/resuming
  // the target's actual producer, closing their connection), this just sends
  // the request. See meet_sfu/src/signaling.js's moderateMute/removeParticipant.
  const moderateMute = useCallback(async (targetUserId, muted, locked) => {
    try {
      await sfuRpc('moderateMute', { targetUserId, muted, locked });
      setHostLockedMap((prev) => ({ ...prev, [targetUserId]: locked }));
    } catch (e) {
      toast({ title: e.message || 'Failed to update mute state', status: 'error', duration: 3000 });
    }
  }, [sfuRpc, toast]);

  const removeParticipantAction = useCallback((targetUserId, targetDisplayName) => {
    if (!window.confirm(`Remove ${targetDisplayName || 'this participant'} from the meeting?`)) return;
    sfuRpc('removeParticipant', { targetUserId }).catch((e) => {
      toast({ title: e.message || 'Failed to remove participant', status: 'error', duration: 3000 });
    });
    // Also drop them from the roster/chat socket — meet-sfu only owns media,
    // Django owns presence/chat (see meet/meet/consumers.py's force_remove).
    rosterWsRef.current?.send(JSON.stringify({ type: 'remove_participant', target_user_id: targetUserId }));
  }, [sfuRpc, toast]);

  // Recording/streaming — real orchestration happens in meet-sfu
  // (recorderOrchestrator.js creating/deleting the meet_recorder k8s Job),
  // this just sends the moderate-gated request and reflects busy/error state
  // locally; isRecording/isStreaming themselves come from the
  // recordingStarted/streamingStarted broadcast above, not from these calls
  // succeeding, since every participant (not just the one who clicked)
  // needs to see the same state.
  const startRecording = useCallback(async () => {
    setRecordingBusy(true);
    try {
      await sfuRpc('startRecording', {});
    } catch (e) {
      toast({ title: 'Failed to start recording', description: e.message, status: 'error', duration: 4000 });
    } finally {
      setRecordingBusy(false);
    }
  }, [sfuRpc, toast]);

  const stopRecording = useCallback(async () => {
    setRecordingBusy(true);
    try {
      await sfuRpc('stopRecording', {});
    } catch (e) {
      toast({ title: 'Failed to stop recording', description: e.message, status: 'error', duration: 4000 });
    } finally {
      setRecordingBusy(false);
    }
  }, [sfuRpc, toast]);

  const startStreaming = useCallback(async (rtmpUrl, rtmpStreamKey) => {
    setStreamingBusy(true);
    try {
      await sfuRpc('startStreaming', { rtmpUrl, rtmpStreamKey });
      onStreamModalClose();
      setStreamUrlInput('');
      setStreamKeyInput('');
    } catch (e) {
      toast({ title: 'Failed to start streaming', description: e.message, status: 'error', duration: 4000 });
    } finally {
      setStreamingBusy(false);
    }
  }, [sfuRpc, toast, onStreamModalClose]);

  const stopStreaming = useCallback(async () => {
    setStreamingBusy(true);
    try {
      await sfuRpc('stopStreaming', {});
    } catch (e) {
      toast({ title: 'Failed to stop streaming', description: e.message, status: 'error', duration: 4000 });
    } finally {
      setStreamingBusy(false);
    }
  }, [sfuRpc, toast]);

  // The admin's locked destination (roomConfig.rtmp_locked) wins server-side
  // regardless of what's sent — only prompt for a URL/key when there's no
  // lock, matching the scoping decision (admin lock takes priority, otherwise
  // the moderator supplies their own).
  const handleStreamButtonClick = useCallback(() => {
    if (isStreaming) { stopStreaming(); return; }
    if (roomConfig.rtmp_locked) { startStreaming('', ''); return; }
    onStreamModalOpen();
  }, [isStreaming, roomConfig.rtmp_locked, stopStreaming, startStreaming, onStreamModalOpen]);

  // meet-sfu: mediasoup signaling + transports. Waits on the same readiness
  // gates as the old mesh implementation did (local media + ICE servers)
  // before opening the connection.
  useEffect(() => {
    if (!joinToken || !mediaReady || !iceServersReady) return;

    let cancelled = false;
    const ws = new WebSocket(getSfuWsUrl());
    sfuWsRef.current = ws;

    ws.onopen = async () => {
      console.log('[Meet] SFU signaling connected');
      try {
        const routerRtpCapabilities = await sfuRpc('getRouterRtpCapabilities');
        if (cancelled) return;
        const device = new Device();
        await device.load({ routerRtpCapabilities });
        deviceRef.current = device;

        // No point creating a send transport at all for a view-only
        // participant (e.g. a webinar attendee) — meet-sfu would reject any
        // produce() on it anyway (permissions.publish enforced server-side
        // in meet_sfu/src/signaling.js), this just avoids the wasted
        // transport/ICE negotiation.
        if (canPublish.current) {
          const sendInfo = await sfuRpc('createTransport', { direction: 'send' });
          if (cancelled) return;
          const sendTransport = device.createSendTransport({
            id: sendInfo.id,
            iceParameters: sendInfo.iceParameters,
            iceCandidates: sendInfo.iceCandidates,
            dtlsParameters: sendInfo.dtlsParameters,
            iceServers: iceServersRef.current,
          });
          sendTransport.on('connect', ({ dtlsParameters }, callback, errback) => {
            sfuRpc('connectTransport', { transportId: sendTransport.id, dtlsParameters }).then(callback).catch(errback);
          });
          sendTransport.on('produce', ({ kind, rtpParameters, appData }, callback, errback) => {
            sfuRpc('produce', { transportId: sendTransport.id, kind, rtpParameters, appData })
              .then(({ id }) => callback({ id }))
              .catch(errback);
          });
          sendTransport.on('connectionstatechange', (state) => handleTransportStateChange(sendTransport, state));
          sendTransportRef.current = sendTransport;

          if (localStreamRef.current) {
            const audioTrack = localStreamRef.current.getAudioTracks()[0];
            const videoTrack = localStreamRef.current.getVideoTracks()[0];
            if (audioTrack) audioProducerRef.current = await sendTransport.produce({ track: audioTrack });
            if (videoTrack) {
              videoProducerRef.current = await sendTransport.produce({
                track: videoTrack,
                encodings: [{ maxBitrate: 1500000 }],
              });
            }
          }
        }

        const recvInfo = await sfuRpc('createTransport', { direction: 'recv' });
        if (cancelled) return;
        const recvTransport = device.createRecvTransport({
          id: recvInfo.id,
          iceParameters: recvInfo.iceParameters,
          iceCandidates: recvInfo.iceCandidates,
          dtlsParameters: recvInfo.dtlsParameters,
          iceServers: iceServersRef.current,
        });
        recvTransport.on('connect', ({ dtlsParameters }, callback, errback) => {
          sfuRpc('connectTransport', { transportId: recvTransport.id, dtlsParameters }).then(callback).catch(errback);
        });
        recvTransport.on('connectionstatechange', (state) => handleTransportStateChange(recvTransport, state));
        recvTransportRef.current = recvTransport;
      } catch (e) {
        console.error('[Meet] Failed to set up mediasoup', e);
        toast({ title: 'Could not connect to media server', status: 'error', duration: 4000 });
      }
    };

    ws.onmessage = (event) => {
      const msg = JSON.parse(event.data);

      if (msg.reqId) {
        const pending = sfuPendingRef.current[msg.reqId];
        if (pending) {
          delete sfuPendingRef.current[msg.reqId];
          msg.ok ? pending.resolve(msg.data) : pending.reject(new Error(msg.error));
        }
        return;
      }

      switch (msg.type) {
        case 'newProducer':
          if (msg.userId !== localUserId.current) {
            consumeProducer(msg.producerId, msg.userId, msg.kind);
          }
          break;

        case 'producerClosed': {
          const meta = producerMetaRef.current[msg.producerId];
          if (meta) {
            consumersRef.current[msg.producerId]?.close();
            delete consumersRef.current[msg.producerId];
            delete producerMetaRef.current[msg.producerId];
            if (remoteTracksRef.current[meta.userId]) {
              remoteTracksRef.current[meta.userId][meta.kind] = null;
            }
            updateParticipantStream(meta.userId);
          }
          break;
        }

        // Host moderation landing on *this* client — meet_sfu already did
        // the real work (paused/resumed our producer, or is about to close
        // this connection); this just reflects it in our own UI and, for
        // mute, re-broadcasts mute_state so everyone else's roster badge
        // updates the same way it would for a normal self-toggle.
        case 'youWereMuted':
        case 'youWereUnmuted': {
          const nowMuted = msg.type === 'youWereMuted';
          if (localStreamRef.current) {
            localStreamRef.current.getAudioTracks().forEach((t) => { t.enabled = !nowMuted; });
          }
          setAudioMuted(nowMuted);
          setMuteLockedByHost(nowMuted ? !!msg.locked : false);
          currentMuteStateRef.current.audioMuted = nowMuted;
          rosterWsRef.current?.send(JSON.stringify({
            type: 'mute_state',
            audio_muted: nowMuted,
            video_muted: currentMuteStateRef.current.videoMuted,
          }));
          toast({
            title: nowMuted
              ? (msg.locked ? 'The host muted you and locked unmuting' : 'The host muted you')
              : 'The host unmuted you',
            status: 'info',
            duration: 3000,
          });
          break;
        }

        case 'removedFromRoom':
          if (hasBeenRemovedRef.current) break;
          hasBeenRemovedRef.current = true;
          toast({ title: 'You were removed from the meeting by the host', status: 'warning', duration: 4000 });
          localStreamRef.current?.getTracks().forEach((t) => t.stop());
          imageStreamRef.current?.getTracks().forEach((t) => t.stop());
          rosterWsRef.current?.close();
          navigate('/');
          break;

        // Recording/streaming state — broadcast to every participant (not
        // just moderators), which is also what drives the "this call is
        // being recorded" transparency badge in the header below.
        case 'recordingStarted':
          setIsRecording(true);
          setRecordingHealth(null);
          toast({ title: 'Recording started', status: 'info', duration: 3000 });
          break;
        case 'recordingStopped':
          setIsRecording(false);
          setRecordingHealth(null);
          toast({ title: 'Recording stopped', status: 'info', duration: 3000 });
          break;
        case 'streamingStarted':
          setIsStreaming(true);
          setStreamingHealth(null);
          toast({ title: 'Live stream started', status: 'info', duration: 3000 });
          break;
        case 'streamingStopped':
          setIsStreaming(false);
          setStreamingHealth(null);
          toast({ title: 'Live stream stopped', status: 'info', duration: 3000 });
          break;

        // From the recorder itself (recorderClient.js's recvTransport
        // connectionstatechange, relayed by meet-sfu) — moderator-only
        // signal for whether it's actually capturing, not just running.
        case 'recordingHealth': {
          const setHealth = msg.kind === 'stream' ? setStreamingHealth : setRecordingHealth;
          setHealth(msg.connectionState);
          if (canModerate.current && (msg.connectionState === 'disconnected' || msg.connectionState === 'failed')) {
            toast({
              title: `${msg.kind === 'stream' ? 'Streaming' : 'Recording'} connection lost`,
              description: 'The recorder appears to have disconnected — content may not be captured. Consider stopping and restarting it.',
              status: 'error',
              duration: 8000,
              isClosable: true,
            });
          }
          break;
        }

        default:
          break;
      }
    };

    ws.onclose = () => {
      console.log('[Meet] SFU signaling disconnected');
    };

    return () => {
      cancelled = true;
      ws.close();
      Object.values(consumersRef.current).forEach((c) => c.close());
      consumersRef.current = {};
      producerMetaRef.current = {};
      remoteTracksRef.current = {};
      audioProducerRef.current?.close();
      videoProducerRef.current?.close();
      audioProducerRef.current = null;
      videoProducerRef.current = null;
      sendTransportRef.current?.close();
      recvTransportRef.current?.close();
      sendTransportRef.current = null;
      recvTransportRef.current = null;
      deviceRef.current = null;
    };
  }, [joinToken, getSfuWsUrl, mediaReady, iceServersReady, sfuRpc, consumeProducer, updateParticipantStream, handleTransportStateChange, toast, navigate]);

  const toggleAudio = useCallback(async () => {
    if (!localStreamRef.current) return;
    const newMuted = !audioMuted;
    if (!newMuted && muteLockedByHost) {
      // The real block is server-side (meet_sfu rejects resumeProducer while
      // muteLocked) — this is just avoiding the round-trip for the obvious case.
      toast({ title: 'The host has locked your mute — you cannot unmute yourself', status: 'warning', duration: 3000 });
      return;
    }
    localStreamRef.current.getAudioTracks().forEach((t) => { t.enabled = !newMuted; });
    setAudioMuted(newMuted);
    currentMuteStateRef.current.audioMuted = newMuted;
    rosterWsRef.current?.send(JSON.stringify({ type: 'mute_state', audio_muted: newMuted, video_muted: videoMuted }));
    if (audioProducerRef.current) {
      try {
        await sfuRpc(newMuted ? 'pauseProducer' : 'resumeProducer', { producerId: audioProducerRef.current.id });
      } catch (e) {
        // Only real failure mode here in practice is the server rejecting an
        // unmute because it's locked (e.g. a lock landed between the check
        // above and this call) — reflect that back rather than leaving the
        // UI showing unmuted while the producer is actually still paused.
        setAudioMuted(true);
        currentMuteStateRef.current.audioMuted = true;
        localStreamRef.current.getAudioTracks().forEach((t) => { t.enabled = false; });
        setMuteLockedByHost(true);
        rosterWsRef.current?.send(JSON.stringify({ type: 'mute_state', audio_muted: true, video_muted: videoMuted }));
        toast({ title: e.message || 'Could not update mute state', status: 'error', duration: 3000 });
      }
    }
  }, [audioMuted, videoMuted, muteLockedByHost, sfuRpc, toast]);

  const toggleVideo = useCallback(() => {
    if (localStreamRef.current) {
      const newMuted = !videoMuted;
      localStreamRef.current.getVideoTracks().forEach((t) => { t.enabled = !newMuted; });
      setVideoMuted(newMuted);
      currentMuteStateRef.current.videoMuted = newMuted;
      rosterWsRef.current?.send(JSON.stringify({ type: 'mute_state', audio_muted: audioMuted, video_muted: newMuted }));
    }
  }, [audioMuted, videoMuted]);

  const toggleScreenShare = useCallback(async () => {
    if (screenSharing) {
      screenStreamRef.current?.getTracks().forEach((t) => t.stop());
      screenStreamRef.current = null;
      setScreenSharing(false);
      rosterWsRef.current?.send(JSON.stringify({ type: 'screenshare_stopped' }));
      const cameraTrack = localStreamRef.current?.getVideoTracks()[0];
      if (cameraTrack && videoProducerRef.current) {
        await videoProducerRef.current.replaceTrack({ track: cameraTrack });
      }
    } else {
      try {
        const stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
        screenStreamRef.current = stream;
        setScreenSharing(true);
        rosterWsRef.current?.send(JSON.stringify({ type: 'screenshare_started' }));
        stream.getVideoTracks()[0].onended = async () => {
          setScreenSharing(false);
          rosterWsRef.current?.send(JSON.stringify({ type: 'screenshare_stopped' }));
          const cameraTrack = localStreamRef.current?.getVideoTracks()[0];
          if (cameraTrack && videoProducerRef.current) {
            await videoProducerRef.current.replaceTrack({ track: cameraTrack });
          }
        };
        // One producer to replace, rather than the mesh's per-peer-connection
        // sender.replaceTrack() loop — every consumer downstream just sees the
        // same producer's track change.
        if (videoProducerRef.current) {
          await videoProducerRef.current.replaceTrack({ track: stream.getVideoTracks()[0] });
        }
      } catch (e) {
        toast({ title: 'Screen share cancelled', status: 'info', duration: 2000 });
      }
    }
  }, [screenSharing, toast]);

  const sendChatMessage = useCallback((message) => {
    rosterWsRef.current?.send(JSON.stringify({ type: 'chat_message', message }));
  }, []);

  const stopImageShare = useCallback(async () => {
    imageStreamRef.current?.getTracks().forEach((t) => t.stop());
    imageStreamRef.current = null;
    const cameraTrack = localStreamRef.current?.getVideoTracks()[0];
    if (cameraTrack && videoProducerRef.current) {
      await videoProducerRef.current.replaceTrack({ track: cameraTrack });
    }
    if (localStreamRef.current) {
      setParticipants((prev) => prev.map((p) =>
        p.isLocal ? { ...p, stream: localStreamRef.current } : p
      ));
    }
    setImageSharing(false);
    rosterWsRef.current?.send(JSON.stringify({ type: 'image_share_stopped' }));
  }, []);

  const startImageShare = useCallback((file) => {
    const canvas = document.createElement('canvas');
    canvas.width = 1280;
    canvas.height = 720;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = async () => {
      // Letterbox the image to fit 16:9
      const scale = Math.min(canvas.width / img.width, canvas.height / img.height);
      const x = (canvas.width - img.width * scale) / 2;
      const y = (canvas.height - img.height * scale) / 2;
      ctx.fillStyle = '#111';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
      URL.revokeObjectURL(objectUrl);

      const stream = canvas.captureStream(0);
      imageStreamRef.current = stream;
      const imageTrack = stream.getVideoTracks()[0];

      if (videoProducerRef.current) {
        await videoProducerRef.current.replaceTrack({ track: imageTrack });
      }

      // Update local display with audio + image track
      const audioTracks = localStreamRef.current?.getAudioTracks() || [];
      const displayStream = new MediaStream([...audioTracks, imageTrack]);
      setParticipants((prev) => prev.map((p) =>
        p.isLocal ? { ...p, stream: displayStream } : p
      ));
      setImageSharing(true);
      rosterWsRef.current?.send(JSON.stringify({ type: 'image_share_started' }));
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      toast({ title: 'Failed to load image', status: 'error', duration: 3000 });
    };
    img.src = objectUrl;
  }, [toast]);

  const toggleImageShare = useCallback(() => {
    if (imageSharing) {
      stopImageShare();
    } else {
      imageInputRef.current?.click();
    }
  }, [imageSharing, stopImageShare]);

  const leaveRoom = useCallback(() => {
    imageStreamRef.current?.getTracks().forEach((t) => t.stop());
    localStreamRef.current?.getTracks().forEach((t) => t.stop());
    rosterWsRef.current?.close();
    sfuWsRef.current?.close();
    navigate('/');
  }, [navigate]);

  const gridCols = participants.length <= 1 ? 1 : participants.length <= 4 ? 2 : 3;

  return (
    <Flex direction="column" h="100vh" bg="gray.900">
      {/* Header */}
      <Flex px={6} py={3} align="center" justify="space-between" bg="gray.800" borderBottom="1px solid" borderColor="gray.700">
        <HStack spacing={3}>
          <Text color="white" fontWeight="600">{roomId}</Text>
          <Badge colorScheme="green" variant="solid">Live</Badge>
          <Badge colorScheme="gray">{participants.length} participant{participants.length !== 1 ? 's' : ''}</Badge>
          {/* Transparency: shown to every participant, not just moderators —
              same broadcast (recordingStarted/streamingStarted) drives it
              for everyone, so there's no way to be recorded/streamed without
              this appearing for the whole call. */}
          {isRecording && (
            <HStack spacing={1}>
              <Badge colorScheme="red" variant="solid" display="flex" alignItems="center" gap={1}>
                <FiDisc size={10} /> Recording
              </Badge>
              {/* Real-time capture health — moderator-only: an attendee
                  seeing "disconnected" can't act on it, a moderator can. */}
              {canModerate.current && (
                <Tooltip label={healthDotLabel(recordingHealth)}>
                  <Circle size="10px" bg={healthDotColor(recordingHealth)} />
                </Tooltip>
              )}
            </HStack>
          )}
          {isStreaming && (
            <HStack spacing={1}>
              <Badge colorScheme="purple" variant="solid" display="flex" alignItems="center" gap={1}>
                <FiRadio size={10} /> Live streaming
              </Badge>
              {canModerate.current && (
                <Tooltip label={healthDotLabel(streamingHealth)}>
                  <Circle size="10px" bg={healthDotColor(streamingHealth)} />
                </Tooltip>
              )}
            </HStack>
          )}
        </HStack>
        <HStack>
          <Tooltip label="Participants">
            <Box position="relative" display="inline-flex">
              <IconButton icon={<FiUsers />} variant="ghost" colorScheme="whiteAlpha" color="white"
                onClick={onParticipantsOpen} aria-label="Participants" />
              <Circle
                size="18px"
                bg="gray.600"
                color="white"
                fontSize="10px"
                fontWeight="bold"
                position="absolute"
                top="2px"
                right="2px"
                pointerEvents="none"
              >
                {participants.length}
              </Circle>
            </Box>
          </Tooltip>
          <Tooltip label="Chat">
            <Box position="relative" display="inline-flex">
              <IconButton icon={<FiMessageSquare />} variant="ghost" colorScheme="whiteAlpha" color="white"
                onClick={onChatOpen} aria-label="Chat" />
              {unreadCount > 0 && (
                <Circle
                  size="18px"
                  bg="red.500"
                  color="white"
                  fontSize="10px"
                  fontWeight="bold"
                  position="absolute"
                  top="2px"
                  right="2px"
                  pointerEvents="none"
                >
                  {unreadCount > 9 ? '9+' : unreadCount}
                </Circle>
              )}
            </Box>
          </Tooltip>
        </HStack>
      </Flex>

      {/* Video Grid */}
      <Box flex={1} p={4} overflow="hidden">
        <Grid templateColumns={`repeat(${gridCols}, 1fr)`} gap={3} h="full">
          {participants.map((p) => (
            <ParticipantTile
              key={p.userId}
              participant={p}
              isLocal={p.isLocal}
              canModerate={canModerate.current}
              locked={!!hostLockedMap[p.userId]}
              onMute={(targetUserId, muted) => moderateMute(targetUserId, muted, false)}
              onLock={(targetUserId, locked) => moderateMute(targetUserId, true, locked)}
              onRemove={removeParticipantAction}
            />
          ))}
        </Grid>
      </Box>

      {/* Controls */}
      <Flex py={4} justify="center" bg="gray.800" borderTop="1px solid" borderColor="gray.700">
        <HStack spacing={3}>
          <Tooltip label={muteLockedByHost ? 'Muted and locked by host' : audioMuted ? 'Unmute' : 'Mute'}>
            <IconButton
              icon={audioMuted ? <FiMicOff /> : <FiMic />}
              borderRadius="full"
              size="lg"
              bg={audioMuted ? 'red.500' : 'gray.600'}
              color="white"
              opacity={muteLockedByHost ? 0.6 : 1}
              _hover={{ bg: audioMuted ? 'red.400' : 'gray.500' }}
              onClick={toggleAudio}
              aria-label="Toggle audio"
            />
          </Tooltip>
          <Tooltip label={videoMuted ? 'Start video' : 'Stop video'}>
            <IconButton
              icon={videoMuted ? <FiVideoOff /> : <FiVideo />}
              borderRadius="full"
              size="lg"
              bg={videoMuted ? 'red.500' : 'gray.600'}
              color="white"
              _hover={{ bg: videoMuted ? 'red.400' : 'gray.500' }}
              onClick={toggleVideo}
              aria-label="Toggle video"
            />
          </Tooltip>
          <Tooltip label={screenSharing ? 'Stop sharing' : 'Share screen'}>
            <IconButton
              icon={<FiMonitor />}
              borderRadius="full"
              size="lg"
              bg={screenSharing ? 'teal.500' : 'gray.600'}
              color="white"
              _hover={{ bg: screenSharing ? 'teal.400' : 'gray.500' }}
              onClick={toggleScreenShare}
              aria-label="Toggle screen share"
            />
          </Tooltip>
          <Tooltip label={imageSharing ? 'Stop sharing image' : 'Share image'}>
            <IconButton
              icon={<FiImage />}
              borderRadius="full"
              size="lg"
              bg={imageSharing ? 'orange.500' : 'gray.600'}
              color="white"
              _hover={{ bg: imageSharing ? 'orange.400' : 'gray.500' }}
              onClick={toggleImageShare}
              aria-label="Toggle image share"
            />
          </Tooltip>
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files[0];
              e.target.value = '';
              if (file) startImageShare(file);
            }}
          />
          {canModerate.current && roomConfig.recording_enabled && (
            <Tooltip label={isRecording ? 'Stop recording' : 'Start recording'}>
              <IconButton
                icon={<FiDisc />}
                borderRadius="full"
                size="lg"
                bg={isRecording ? 'red.600' : 'gray.600'}
                color="white"
                _hover={{ bg: isRecording ? 'red.500' : 'gray.500' }}
                isLoading={recordingBusy}
                onClick={isRecording ? stopRecording : startRecording}
                aria-label="Toggle recording"
              />
            </Tooltip>
          )}
          {canModerate.current && roomConfig.streaming_enabled && (
            <Tooltip label={isStreaming ? 'Stop live stream' : 'Start live stream'}>
              <IconButton
                icon={<FiRadio />}
                borderRadius="full"
                size="lg"
                bg={isStreaming ? 'purple.600' : 'gray.600'}
                color="white"
                _hover={{ bg: isStreaming ? 'purple.500' : 'gray.500' }}
                isLoading={streamingBusy}
                onClick={handleStreamButtonClick}
                aria-label="Toggle live stream"
              />
            </Tooltip>
          )}
          <Tooltip label="Leave">
            <IconButton
              icon={<FiPhoneOff />}
              borderRadius="full"
              size="lg"
              bg="red.600"
              color="white"
              _hover={{ bg: 'red.500' }}
              onClick={leaveRoom}
              aria-label="Leave room"
            />
          </Tooltip>
        </HStack>
      </Flex>

      {/* Chat Drawer */}
      <Drawer isOpen={isChatOpen} onClose={onChatClose} placement="right" size="sm">
        <DrawerOverlay />
        <DrawerContent bg="gray.800">
          <DrawerHeader color="white" borderBottom="1px solid" borderColor="gray.700">
            Chat
          </DrawerHeader>
          <DrawerBody p={0}>
            <ChatPanel messages={chatMessages} onSend={sendChatMessage} />
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Participants Drawer */}
      <Drawer isOpen={isParticipantsOpen} onClose={onParticipantsClose} placement="right" size="sm">
        <DrawerOverlay />
        <DrawerContent bg="gray.800">
          <DrawerHeader color="white" borderBottom="1px solid" borderColor="gray.700">
            Participants ({participants.length})
          </DrawerHeader>
          <DrawerBody p={0}>
            <ParticipantsPanel
              participants={participants}
              canModerate={canModerate.current}
              lockedMap={hostLockedMap}
              onMute={(targetUserId, muted) => moderateMute(targetUserId, muted, false)}
              onLock={(targetUserId, locked) => moderateMute(targetUserId, true, locked)}
              onRemove={removeParticipantAction}
            />
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Stream destination — only shown when the admin hasn't locked one
          (roomConfig.rtmp_locked); see handleStreamButtonClick above. */}
      <Modal isOpen={isStreamModalOpen} onClose={onStreamModalClose} isCentered>
        <ModalOverlay />
        <ModalContent bg="gray.800" color="white">
          <ModalHeader>Start live stream</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={3} align="stretch">
              <FormControl>
                <FormLabel fontSize="sm">RTMP URL</FormLabel>
                <Input
                  placeholder="rtmp://a.rtmp.youtube.com/live2"
                  value={streamUrlInput}
                  onChange={(e) => setStreamUrlInput(e.target.value)}
                  bg="gray.700"
                  border="none"
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Stream key</FormLabel>
                <Input
                  placeholder="Stream key"
                  value={streamKeyInput}
                  onChange={(e) => setStreamKeyInput(e.target.value)}
                  bg="gray.700"
                  border="none"
                  type="password"
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onStreamModalClose}>Cancel</Button>
            <Button
              colorScheme="purple"
              isLoading={streamingBusy}
              isDisabled={!streamUrlInput || !streamKeyInput}
              onClick={() => startStreaming(streamUrlInput, streamKeyInput)}
            >
              Go live
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
}
